import moment from 'moment';

export enum TIME_FORMAT {
  AM = 'AM',
  PM = 'PM',
}

export const defaultDate: string = '--/--/----';
export const defaultDateFormat: string = 'DD/MM/YYYY';

export const dateFormatForRequest: string = 'YYYY-MM-DD';
export const dateTimeFormatForRequest: string = 'YYYY-MM-DD HH:mm:ss';

export const defaultTime: Date = moment({ hour: 6, minute: 30, second: 0 }).toDate();
export const defaultTimeFrom: Date = new Date('1970-01-01 00:00:00');
export const defaultTimeTo: Date = new Date('1970-01-01 23:59:59');

/**
 * @enum {number} - week days from 1 - Monday to 7 - Sunday
 */
export enum DAYS_OF_WEEK {
  MONDAY = 1,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
}

/**
 * @see DAYS_OF_WEEK
 * @const {Array.<DAYS_OF_WEEK>} - full week days
 */
export const fullWeek: DAYS_OF_WEEK[] = [
  DAYS_OF_WEEK.SUNDAY,
  DAYS_OF_WEEK.MONDAY,
  DAYS_OF_WEEK.TUESDAY,
  DAYS_OF_WEEK.WEDNESDAY,
  DAYS_OF_WEEK.THURSDAY,
  DAYS_OF_WEEK.FRIDAY,
  DAYS_OF_WEEK.SATURDAY,
];

export const DateOfWeekMap: Record<DAYS_OF_WEEK, string> = {
  [DAYS_OF_WEEK.MONDAY]: 'monday',
  [DAYS_OF_WEEK.TUESDAY]: 'tuesday',
  [DAYS_OF_WEEK.WEDNESDAY]: 'wednesday',
  [DAYS_OF_WEEK.THURSDAY]: 'thursday',
  [DAYS_OF_WEEK.FRIDAY]: 'friday',
  [DAYS_OF_WEEK.SATURDAY]: 'saturday',
  [DAYS_OF_WEEK.SUNDAY]: 'sunday',
};

export enum DateRanges {
  TodayRamadan5AMto5AM = 0,
  // Today = 0,
  TodayUntilNow = 1,
  Yesterday = 2,
  Tomorrow = 3,
  ThisWeek = 4,
  ThisWeekUntilNow = 5,
  ThisWeekUntilTodayRamadan5AMto5AM = 6,
  LastWeek = 7,
  ThisMonth = 8,
  ThisMonthUntilNow = 9,
  ThisMonthUntilTodayRamadan5AMto5AM = 10,
  LastMonth = 11,
  ThisYear = 12,
  ThisYearUntilNow = 13,
  ThisYearUntilToday = 14,
  LastYear = 15,
  Next30Days = 16,
  Next90Days = 17,
  Today = 18,
  ThisWeekUntilToday = 19,
  ThisMonthUntilToday = 20,
}

export const presetDatesDataMap = {
  [DateRanges.Today]: {
    title: 'txt_today',
    preset: DateRanges.Today,
    getRange: () => ({
      from: moment().startOf('day').unix() * 1000,
      to: moment().endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.TodayUntilNow]: {
    title: 'txt_today',
    preset: DateRanges.TodayUntilNow,
    getRange: () => ({
      from: moment().startOf('day').unix() * 1000,
      to: Date.now(),
    }),
  },
  [DateRanges.Yesterday]: {
    title: 'yesterday',
    preset: DateRanges.Yesterday,
    getRange: () => ({
      from: moment().subtract(1, 'day').startOf('day').unix() * 1000,
      to: moment().subtract(1, 'day').endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.Tomorrow]: {
    title: 'txt_tomorrow',
    preset: DateRanges.Tomorrow,
    getRange: () => ({
      from: moment().add(1, 'day').startOf('day').unix() * 1000,
      to: moment().add(1, 'day').endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.ThisWeek]: {
    title: 'this_week',
    preset: DateRanges.ThisWeek,
    getRange: () => ({
      from: moment().startOf('week').unix() * 1000,
      to: moment().endOf('week').unix() * 1000,
    }),
  },
  [DateRanges.ThisWeekUntilNow]: {
    title: 'this_week',
    preset: DateRanges.ThisWeekUntilNow,
    getRange: () => ({
      from: moment().startOf('week').unix() * 1000,
      to: Date.now(),
    }),
  },
  [DateRanges.ThisWeekUntilToday]: {
    title: 'this_week',
    preset: DateRanges.ThisWeekUntilToday,
    getRange: () => ({
      from: moment().startOf('week').unix() * 1000,
      to: moment().endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.LastWeek]: {
    title: 'last_week',
    preset: DateRanges.LastWeek,
    getRange: () => ({
      from: moment().subtract(1, 'week').startOf('week').unix() * 1000,
      to: moment().subtract(1, 'week').endOf('week').unix() * 1000,
    }),
  },
  [DateRanges.ThisMonth]: {
    title: 'this_month',
    preset: DateRanges.ThisMonth,
    getRange: () => ({
      from: moment().startOf('month').unix() * 1000,
      to: moment().endOf('month').unix() * 1000,
    }),
  },
  [DateRanges.ThisMonthUntilNow]: {
    title: 'this_month',
    preset: DateRanges.ThisMonthUntilNow,
    getRange: () => ({
      from: moment().startOf('month').unix() * 1000,
      to: Date.now(),
    }),
  },
  [DateRanges.ThisMonthUntilToday]: {
    title: 'this_month',
    preset: DateRanges.ThisMonthUntilToday,
    getRange: () => ({
      from: moment().startOf('month').unix() * 1000,
      to: moment().endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.LastMonth]: {
    title: 'last_month',
    preset: DateRanges.LastMonth,
    getRange: () => ({
      from: moment().subtract(1, 'month').startOf('month').unix() * 1000,
      to: moment().subtract(1, 'month').endOf('month').unix() * 1000,
    }),
  },
  [DateRanges.Next30Days]: {
    title: 'next_30_days',
    preset: DateRanges.Next30Days,
    getRange: () => ({
      from: moment().add(1, 'day').startOf('day').unix() * 1000,
      to: moment().add(30, 'days').endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.Next90Days]: {
    title: 'next_90_days',
    preset: DateRanges.Next90Days,
    getRange: () => ({
      from: moment().add(1, 'day').startOf('day').unix() * 1000,
      to: moment().add(90, 'days').endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.ThisYear]: {
    title: 'this_year',
    preset: DateRanges.ThisYear,
    getRange: () => ({
      from: moment().startOf('year').unix() * 1000,
      to: moment().endOf('year').unix() * 1000,
    }),
  },
  [DateRanges.ThisYearUntilNow]: {
    title: 'this_year',
    preset: DateRanges.ThisYearUntilNow,
    getRange: () => ({
      from: moment().startOf('year').unix() * 1000,
      to: Date.now(),
    }),
  },
  [DateRanges.ThisYearUntilToday]: {
    title: 'this_year',
    preset: DateRanges.ThisYearUntilToday,
    getRange: () => ({
      from: moment().startOf('year').unix() * 1000,
      to: moment().endOf('day').unix() * 1000,
    }),
  },
  [DateRanges.LastYear]: {
    title: 'last_year',
    preset: DateRanges.LastYear,
    getRange: () => ({
      from: moment().subtract(1, 'year').startOf('year').unix() * 1000,
      to: moment().subtract(1, 'year').endOf('year').unix() * 1000,
    }),
  },
  [DateRanges.TodayRamadan5AMto5AM]: {
    title: 'txt_today',
    preset: DateRanges.TodayRamadan5AMto5AM,
    getRange: () => ({
      from:
        moment().hour() >= 5
          ? moment().startOf('day').add(5, 'hours').unix() * 1000
          : moment().subtract(1, 'day').startOf('day').add(5, 'hours').unix() * 1000,
      to:
        moment().hour() >= 5
          ? moment().add(1, 'day').startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000
          : moment().startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000,
    }),
  },
  [DateRanges.ThisWeekUntilTodayRamadan5AMto5AM]: {
    title: 'this_week',
    preset: DateRanges.ThisWeekUntilTodayRamadan5AMto5AM,
    getRange: () => ({
      from:
        moment().hour() >= 5
          ? moment().startOf('week').add(5, 'hours').unix() * 1000
          : moment().startOf('week').isSame(moment(), 'day')
          ? moment().subtract(1, 'day').startOf('week').add(5, 'hours').unix() * 1000
          : moment().startOf('week').add(5, 'hours').unix() * 1000,
      to:
        moment().hour() >= 5
          ? moment().add(1, 'day').startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000
          : moment().startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000,
    }),
  },
  [DateRanges.ThisMonthUntilTodayRamadan5AMto5AM]: {
    title: 'this_month',
    preset: DateRanges.ThisMonthUntilTodayRamadan5AMto5AM,
    getRange: () => ({
      from:
        moment().hour() >= 5
          ? moment().startOf('month').add(5, 'hours').unix() * 1000
          : moment().startOf('month').isSame(moment(), 'day')
          ? moment().subtract(1, 'day').startOf('month').add(5, 'hours').unix() * 1000
          : moment().startOf('month').add(5, 'hours').unix() * 1000,
      to:
        moment().hour() >= 5
          ? moment().add(1, 'day').startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000
          : moment().startOf('day').add(5, 'hours').subtract(1, 'second').unix() * 1000,
    }),
  },
};

export enum TimePickerMode {
  GENERAL = 'general',
  DAYTIME = 'daytime',
  DAYTIME_RANGE = 'daytimeRange',
}
